import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AppContext from '../common/containers/AppContext';
import useGetRequest from '../common/services/useGetRequest';
import RevStatsPageContent from './components/RevStatsPageContent';
import getProducts from './services/data/getProducts';
import getCustomerSummaries from './services/data/getCustomerSummaries';
import getIngestionStatus from './services/data/getIngestionStatus';
import AreaTypes from './services/AreaTypes';
import HierarchyType from './components/HierarchyType';

// replace undefined with null
export const normalizeIds = ({ area, suite, product, category }) => ({
    area: area ?? null,
    suite: suite ?? null,
    product: product ?? null,
    category: category ?? null,
});

// get product ids json to label mapping
function getProductCompoundIdToLabelMap(products) {
    return products.reduce((acc, p) => {
        acc[JSON.stringify(normalizeIds(p))] = p;
        return acc;
    }, {});
}

// If the search text is empty, we want to display the top layer to the user
function getDefaultSearchOptions(products) {
    const defaultKeysSet = new Set([
        AreaTypes.COMMERCIAL_CLOUD,
        AreaTypes.CONSULTING,
        AreaTypes.DATA_CLOUD,
        AreaTypes.DEVELOPMENT_CLOUD,
        AreaTypes.QUALITY_CLOUD,
        AreaTypes.SERVICES,
    ]);

    const defaultKeyToProduct = {};
    products.forEach((product) => {
        if (defaultKeysSet.has(product.label)) {
            defaultKeyToProduct[product.label] = product;
        }
    });

    const defaultSearchOptions = [];
    defaultKeysSet.forEach((key) => {
        if (defaultKeyToProduct[key] !== undefined) {
            defaultSearchOptions.push(defaultKeyToProduct[key]);
        }
    });

    return defaultSearchOptions;
}

export default function RevStatsAuthenticatedContainer() {
    const [user, userIsLoading] = useGetRequest('/ui/session/userinfo');
    const [customers, setCustomers] = useState(null);
    const [products, setProducts] = useState([]);
    const [productIdsToLabelMap, setProductIdsToLabelMap] = useState([]);
    const [defaultSearchOptions, setDefaultSearchOptions] = useState([]);
    const [customerPageOrder, setCustomerPageOrder] = useState(null);
    const [ingestionInProgress, setIngestionInProgress] = useState(false);
    const [ingestionStatusLoaded, setIngestionStatusLoaded] = useState(false);
    const location = useLocation();

    const renderPageContent = useCallback(
        (children) => <RevStatsPageContent>{children}</RevStatsPageContent>,
        [],
    );

    const fetchAppData = useCallback(async () => {
        const [initialCustomers, initialProducts, servicesProducts] = await Promise.all([
            getCustomerSummaries(),
            getProducts(HierarchyType.BASE),
            getProducts(HierarchyType.SERVICES),
        ]);

        setProducts(initialProducts);
        const customerAreaOrder = [
            'Development Cloud',
            'Quality Cloud',
            'Commercial Cloud',
            'Data Cloud',
            'Services',
            'Consulting',
        ];
        const productMap = new Map(initialProducts.map((item) => [item.label, item]));
        const orderedAreasForCustomer = customerAreaOrder
            .map((label) => productMap.get(label))
            .filter((item) => item != null);
        setCustomerPageOrder(orderedAreasForCustomer);

        const productIdToLabelMap = {
            ...getProductCompoundIdToLabelMap(initialProducts),
            ...getProductCompoundIdToLabelMap(servicesProducts),
        };
        setProductIdsToLabelMap(productIdToLabelMap);
        setCustomers(initialCustomers);
        setDefaultSearchOptions(getDefaultSearchOptions(initialProducts));
    }, []);

    useEffect(() => {
        fetchAppData();

        getIngestionStatus()
            .then((status) => {
                if (status != null) {
                    setIngestionInProgress(status);
                }
            })
            .catch(() => {})
            .finally(() => setIngestionStatusLoaded(true));
    }, [fetchAppData]);

    const wasIngestionInProgress = useRef(false);
    useEffect(() => {
        if (ingestionInProgress) {
            wasIngestionInProgress.current = true;
        } else if (ingestionStatusLoaded && wasIngestionInProgress.current) {
            wasIngestionInProgress.current = false;
            fetchAppData();
        }
    }, [ingestionInProgress, ingestionStatusLoaded, fetchAppData]);

    const appContext = useMemo(() => {
        return {
            products,
            productIdsToLabelMap,
            customers,
            defaultSearchOptions,
            renderPageContent,
            user,
            customerPageOrder,
            ingestionInProgress,
            setIngestionInProgress,
            ingestionStatusLoaded,
        };
    }, [
        products,
        productIdsToLabelMap,
        customers,
        defaultSearchOptions,
        renderPageContent,
        user,
        customerPageOrder,
        ingestionInProgress,
        ingestionStatusLoaded,
    ]);

    if (userIsLoading) {
        return null;
    }

    // Outlet will serve any content that a nested <Route /> matches.
    return (
        <AppContext.Provider value={appContext}>
            {ingestionInProgress &&
            location.pathname !== '/ingestion-in-progress' &&
            location.pathname !== '/logs' &&
            location.pathname !== '/help' &&
            !location.pathname.startsWith('/admin') ? (
                <Navigate to="/ingestion-in-progress" replace />
            ) : (
                <Outlet />
            )}
        </AppContext.Provider>
    );
}
